import { CHIP_CONSTANTS } from './chip-constants';
var ChipFoundation = /** @class */ (function () {
    function ChipFoundation(_adapter) {
        var _this = this;
        this._adapter = _adapter;
        this._type = CHIP_CONSTANTS.defaults.TYPE;
        this._selected = false;
        this._disabled = false;
        this._invalid = false;
        this._value = null;
        this._dense = false;
        this._isInitialized = false;
        this._clickListener = function (evt) { return _this._onClick(evt); };
        this._keydownListener = function (evt) { return _this._onKeydown(evt); };
        this._deleteKeydownListener = function (evt) { return _this._onDeleteKeydown(evt); };
        this._buttonFocusListener = function (evt) { return _this._onButtonFocus(evt); };
        this._buttonBlurListener = function (evt) { return _this._onButtonBlur(evt); };
    }
    ChipFoundation.prototype.initialize = function () {
        this._rippleInstance = this._adapter.initializeRipple();
        this._adapter.addButtonListener('click', this._clickListener);
        this._adapter.addButtonListener('focus', this._buttonFocusListener);
        this._adapter.addButtonListener('blur', this._buttonBlurListener);
        this._applyState();
        this._isInitialized = true;
    };
    ChipFoundation.prototype.disconnect = function () {
        this._destroyRipple();
        this._adapter.removeButtonListener('keydown', this._keydownListener);
        this._adapter.removeButtonListener('click', this._clickListener);
        this._adapter.removeButtonListener('focus', this._buttonFocusListener);
        this._adapter.removeButtonListener('blur', this._buttonBlurListener);
        this._isInitialized = false;
    };
    ChipFoundation.prototype._destroyRipple = function () {
        if (this._rippleInstance) {
            this._rippleInstance.destroy();
            this._rippleInstance = undefined;
        }
    };
    ChipFoundation.prototype._applyState = function () {
        // Attempt to inherit state from the parent chip-set component
        // Note: this should only inherit properties that need to be the same across the chips within a set
        var chipSetState = this._adapter.getChipSetState();
        if (chipSetState) {
            this._type = chipSetState.type;
            this._disabled = chipSetState.disabled;
            this._dense = chipSetState.dense;
        }
        // Ensure that only non-action chips can be selected
        if (this._type === 'action') {
            this._selected = false;
        }
        this._applyDisabled();
        this._applyType();
        this._applySelected();
        this._applyDense();
    };
    ChipFoundation.prototype._onClick = function (evt) {
        var target = evt.target;
        var isDeleteButton = target.classList.contains(CHIP_CONSTANTS.classes.DELETE_BUTTON) || target.classList.contains(CHIP_CONSTANTS.classes.DELETE_BUTTON_TOUCH_TARGET);
        if (isDeleteButton) {
            this._handleDeleteInteraction(evt);
        }
        else {
            this._handleSelectInteraction();
        }
    };
    ChipFoundation.prototype._onKeydown = function (evt) {
        if (this._type === 'input') {
            var isDeleteKey = evt.key === 'Delete' || evt.key === 'Backspace';
            if (isDeleteKey) {
                this._emitDeleteEvent();
            }
        }
    };
    ChipFoundation.prototype._onDeleteKeydown = function (evt) {
        if (evt.key === 'Enter') {
            this._handleDeleteInteraction(evt);
        }
    };
    ChipFoundation.prototype._onButtonFocus = function (evt) {
        this._adapter.setHostAttribute('focused');
    };
    ChipFoundation.prototype._onButtonBlur = function (evt) {
        this._adapter.removeHostAttribute('focused');
    };
    ChipFoundation.prototype._handleDeleteInteraction = function (evt) {
        // The delete event can only be fired when the `input` or `field` type is used
        var target = evt.target;
        var isDeleteButton = target.classList.contains(CHIP_CONSTANTS.classes.DELETE_BUTTON) || target.classList.contains(CHIP_CONSTANTS.classes.DELETE_BUTTON_TOUCH_TARGET);
        if (isDeleteButton) {
            evt.stopPropagation();
            this._emitDeleteEvent();
        }
    };
    ChipFoundation.prototype._handleSelectInteraction = function () {
        var wasDefaultPrevented = this._emitSelectEvent();
        var isSelectableType = ['filter', 'choice', 'input'].includes(this._type);
        // We only toggle the selected state for certain types (and if default wasn't prevented on the event)
        if (!wasDefaultPrevented && isSelectableType) {
            this._selected = !this._selected;
            this._applySelected();
        }
    };
    ChipFoundation.prototype._emitSelectEvent = function () {
        var selectEventData = {
            selected: !this._selected,
            value: this._value
        };
        return !this._adapter.emitHostEvent(CHIP_CONSTANTS.events.SELECT, selectEventData, true, true);
    };
    ChipFoundation.prototype._emitDeleteEvent = function () {
        var deleteEventData = { value: this._value };
        this._adapter.emitHostEvent(CHIP_CONSTANTS.events.DELETE, deleteEventData, true, true);
    };
    ChipFoundation.prototype._applyType = function () {
        this._adapter.clearTypeClass();
        this._adapter.removeButtonListener('keydown', this._keydownListener);
        switch (this._type) {
            case 'action':
                this._adapter.addRootClass(CHIP_CONSTANTS.classes.ACTION);
                break;
            case 'choice':
                this._adapter.addRootClass(CHIP_CONSTANTS.classes.CHOICE);
                break;
            case 'filter':
                this._adapter.addRootClass(CHIP_CONSTANTS.classes.FILTER);
                break;
            case 'input':
                this._adapter.addRootClass(CHIP_CONSTANTS.classes.INPUT);
                this._adapter.addButtonListener('keydown', this._keydownListener);
                break;
            case 'field':
                this._adapter.addRootClass(CHIP_CONSTANTS.classes.FIELD);
                this._adapter.addButtonListener('keydown', this._keydownListener);
                break;
        }
        this._adapter.setCheckmarkVisibility(this._type === 'filter');
        var showDeleteButton = this._type === 'input' || this._type === 'field';
        this._adapter.setDeleteButtonVisibility(showDeleteButton, this._deleteKeydownListener);
        this._adapter.setLeadingSlotVisibility(!this._selected);
        this._adapter.setHostAttribute(CHIP_CONSTANTS.attributes.TYPE, this._type);
    };
    ChipFoundation.prototype._applySelected = function () {
        this._adapter.setSelected(this._selected);
        // If using the filter type, we need to hide the leading slot to ensure that
        // the checkmark shows in place of any leading elements
        if (this._type === 'filter') {
            this._adapter.setLeadingSlotVisibility(!this._selected);
        }
        this._adapter.toggleHostAttribute(CHIP_CONSTANTS.attributes.SELECTED, this._selected);
    };
    ChipFoundation.prototype._applyDisabled = function () {
        this._adapter.setDisabled(this._disabled);
        this._adapter.toggleHostAttribute(CHIP_CONSTANTS.attributes.DISABLED, this._disabled);
    };
    ChipFoundation.prototype._applyDense = function () {
        this._adapter.setDense(this._dense);
        this._adapter.toggleHostAttribute(CHIP_CONSTANTS.attributes.DISABLED, this._disabled);
    };
    Object.defineProperty(ChipFoundation.prototype, "type", {
        get: function () {
            return this._type;
        },
        set: function (value) {
            if (this._type !== value) {
                this._type = value;
                if (this._isInitialized) {
                    var isValidType = ['action', 'choice', 'filter', 'input', 'field'].includes(this._type);
                    if (!isValidType) {
                        this._type = 'action';
                    }
                    if (this._type === 'action') {
                        this._selected = false;
                        this._applySelected();
                    }
                    this._applyType();
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ChipFoundation.prototype, "selected", {
        get: function () {
            return this._selected;
        },
        set: function (value) {
            if (this._selected !== value) {
                this._selected = value;
                if (this._isInitialized) {
                    if (this._type === 'action') {
                        this._selected = false;
                    }
                    this._applySelected();
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ChipFoundation.prototype, "disabled", {
        get: function () {
            return this._disabled;
        },
        set: function (value) {
            if (this._disabled !== value) {
                this._disabled = value;
                if (this._isInitialized) {
                    this._applyDisabled();
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ChipFoundation.prototype, "invalid", {
        get: function () {
            return this._invalid;
        },
        set: function (val) {
            if (this._invalid === val) {
                return;
            }
            this._invalid = val;
            if (val) {
                this._adapter.setHostAttribute(CHIP_CONSTANTS.attributes.INVALID, '');
                this._adapter.addRootClass(CHIP_CONSTANTS.classes.INVALID);
            }
            else {
                this._adapter.removeHostAttribute(CHIP_CONSTANTS.attributes.INVALID);
                this._adapter.removeRootClass(CHIP_CONSTANTS.classes.INVALID);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ChipFoundation.prototype, "value", {
        get: function () {
            return this._value;
        },
        set: function (value) {
            this._value = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ChipFoundation.prototype, "dense", {
        get: function () {
            return this._dense;
        },
        set: function (value) {
            if (this._dense !== value) {
                this._dense = value;
                if (this._isInitialized) {
                    this._applyDense();
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    ChipFoundation.prototype.setFocus = function () {
        this._adapter.setFocus();
    };
    return ChipFoundation;
}());
export { ChipFoundation };
